import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CloseOutlinedIcon, DownloadOutlinedIcon, FileIcon, PictureOffOutlinedIcon, } from '@getgo/chameleon-icons/react';
import { IconButton, TooltipV2, Typography } from '@getgo/chameleon-web/react';
import classNames from 'classnames';
import Loading from "../Loading";
import TextTruncate from "../TextTruncate";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './Thumbnail.module.scss';
import ThumbnailDialog from './ThumbnailDialog';
const TYPE_IMAGE = 'image';
const KILOBYTES = 1024;
export const getRfcType = (contentType) => {
    var _a;
    const type = contentType !== null && contentType !== void 0 ? contentType : '';
    const rfcParts = type.split('/');
    return (_a = rfcParts[0]) !== null && _a !== void 0 ? _a : '';
};
export const getFileURL = fileBlob => {
    let fileUrl = '';
    if (fileBlob) {
        try {
            fileUrl = URL.createObjectURL(fileBlob);
        }
        catch (e) {
            // In case it's already an URL and not a blob
            return fileBlob;
        }
    }
    return fileUrl;
};
export const saveFile = async (name, fileUrl) => {
    const linkElement = document.createElement('a');
    document.body.appendChild(linkElement);
    linkElement.setAttribute('style', 'display: none');
    try {
        linkElement.href = fileUrl;
        linkElement.download = name;
        // Setting the target to `_blank` is a failsafe
        // to make sure that any browser interpretable URL (images, PDF)
        // is shown within a new window/tab.
        linkElement.target = '_blank';
        linkElement.click();
    }
    finally {
        document.body.removeChild(linkElement);
    }
};
const FILE_ICON_SIZE = '36px';
export const GenericPreview = ({ file, fileBlob, getFileBlob, onRemove, }) => {
    return (_jsxs(_Fragment, { children: [_jsx(FileIcon, { size: FILE_ICON_SIZE }, void 0), _jsx(TextTruncate, Object.assign({ className: classes.name }, { children: file.name }), void 0), onRemove ? (_jsx(IconButton, Object.assign({ size: "xsmall", className: classes.remove, variant: "secondary", onClick: onRemove }, { children: _jsx(CloseOutlinedIcon, {}, void 0) }), void 0)) : (_jsx(IconButton, Object.assign({ variant: "secondary", onClick: async () => {
                    let url = '';
                    try {
                        url = getFileURL(fileBlob !== null && fileBlob !== void 0 ? fileBlob : (await getFileBlob()));
                        saveFile(file.name, url);
                    }
                    finally {
                        url && URL.revokeObjectURL(url);
                    }
                }, className: classes.download }, { children: _jsx(DownloadOutlinedIcon, {}, void 0) }), void 0))] }, void 0));
};
export const ImagePreview = ({ file, fileBlob, onRemove, getFileBlob, setNotFound, isWebChat, }) => {
    const imageRef = useRef(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showImage, setShowImage] = useState(false);
    useEffect(() => {
        const loadImage = async () => {
            let url = getFileURL(fileBlob);
            if (!url) {
                setLoading(true);
                url = getFileURL(await getFileBlob());
                if (!url) {
                    setNotFound(true);
                }
            }
            if (imageRef.current && url) {
                setFileUrl(url);
                imageRef.current.src = url;
                setLoading(false);
            }
        };
        loadImage();
        return () => {
            fileUrl && URL.revokeObjectURL(fileUrl);
        };
    }, [fileBlob]);
    return (_jsxs(_Fragment, { children: [_jsx("img", { onClick: () => {
                    if (isWebChat) {
                        window.open(fileUrl, '_blank');
                    }
                    else {
                        setShowImage(true);
                    }
                }, className: classNames(classes.image, !onRemove && classes.clickable), ref: imageRef, alt: file.name }, void 0), loading && _jsx(Loading, { className: classes.loading, opaque: true }, void 0), onRemove ? (_jsx(IconButton, Object.assign({ size: "small", className: classes.remove, variant: "secondary", onClick: onRemove }, { children: _jsx(CloseOutlinedIcon, {}, void 0) }), void 0)) : (_jsx(IconButton, Object.assign({ variant: "secondary", onClick: () => saveFile(file.name, fileUrl), className: classes.download }, { children: _jsx(DownloadOutlinedIcon, {}, void 0) }), void 0)), !isWebChat && showImage && (_jsx(ThumbnailDialog, { onClose: () => setShowImage(false), onDownload: !onRemove ? () => saveFile(file.name, fileUrl) : null, open: showImage, title: file.name, fileUrl: fileUrl }, void 0))] }, void 0));
};
const Thumbnail = ({ file, fileBlob, onRemove, getFileBlob, isWebChat, }) => {
    const { t } = useTranslation();
    const rfcType = getRfcType(file.contentType);
    const [notFound, setNotFound] = useState(false);
    return (_jsx(_Fragment, { children: notFound ? (_jsxs("div", Object.assign({ className: classes.notFound }, { children: [_jsx(PictureOffOutlinedIcon, { color: "var(--goto-icon-color-secondary)", size: '21px' }, void 0), _jsx(Typography, Object.assign({ className: classes.text }, { children: t('This attachment is no longer available from the external provider.') }), void 0)] }), void 0)) : (_jsx(TooltipV2, Object.assign({ trigger: _jsx("div", Object.assign({ className: classNames(classes.file, rfcType === TYPE_IMAGE && !onRemove && classes.imageFile) }, { children: rfcType === TYPE_IMAGE ? (_jsx(ImagePreview, { file: file, onRemove: onRemove, getFileBlob: getFileBlob, fileBlob: fileBlob, setNotFound: setNotFound, isWebChat: isWebChat }, void 0)) : (_jsx(GenericPreview, { file: file, onRemove: onRemove, getFileBlob: getFileBlob, fileBlob: fileBlob }, void 0)) }), void 0), position: "bottom-center" }, { children: _jsx(Typography, Object.assign({ color: "text-04" }, { children: file.size
                    ? t('{{sizeInKB}} KB', {
                        sizeInKB: Math.floor(file.size / KILOBYTES),
                    })
                    : t('Unknown file size') }), void 0) }), void 0)) }, void 0));
};
export default Thumbnail;
//# sourceMappingURL=Thumbnail.js.map